import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { getInvoice } from "../../../redux/reducers/Properties/PropertiesSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { currencySymbol } from "shared/CurrencySymbol";
import { InvoiceData } from "../../../redux/reducers/Properties/PropertiesSliceTypes";
import CustomDatePickerV2 from "shared/DateField/CustomDatePickerV2";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import Pagination from "shared/Pagination/Pagination";
import { useNavigate } from "react-router-dom";

interface TotalItems {
  invoice_amt: string;
  confirmed_amt: string;
}

interface InvoiceDataExtended extends InvoiceData {
  paid_at?: string;
}

const formatPaidAt = (paidAt: string): string =>
  moment(paidAt, "MMMM DD, YYYY [at] hh:mm A").format("MMM DD, YYYY");

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; classes: string }> = {
    paid:      { label: "Paid",      classes: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    pending:   { label: "Pending",   classes: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    cancelled: { label: "Cancelled", classes: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400" },
    refunded:  { label: "Refunded",  classes: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  };
  const cfg = map[status] ?? map["pending"];
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

// ─── InvoiceDetailModal ───────────────────────────────────────────────────────
// Key architectural decision: `isOpen` and `invoice` are SEPARATE props.
// When isOpen flips to false, the modal is still mounted but hidden — this means
// NO layout shift occurs while the close animation runs and the close button
// never moves under the user's cursor between mousedown and mouseup.

interface InvoiceDetailModalProps {
  isOpen: boolean;
  invoice: InvoiceDataExtended | null;
  onClose: () => void;
  onViewBooking: (bookingId: number) => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onViewBooking,
}) => {
  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Always render into the portal so the close button is never
  // "born" mid-click due to conditional mounting
  return createPortal(
    <div
      aria-modal="true"
      role="dialog"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        backgroundColor: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        // Hidden via pointer-events + opacity instead of unmounting,
        // so button position is stable when isOpen toggles
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? "auto" : "none",
        transition: "opacity 150ms ease",
      }}
      // Backdrop click — only fires when clicking the backdrop itself
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          borderRadius: "1rem",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          width: "100%",
          maxWidth: "32rem",
          maxHeight: "90vh",
          overflowY: "auto",
          transform: isOpen ? "scale(1)" : "scale(0.97)",
          transition: "transform 150ms ease",
        }}
        className="bg-white dark:bg-gray-900"
      >
        {/* ── Close button ─────────────────────────────────────────────────── */}
        {/* Uses onMouseDown instead of onClick so it fires before any blur/focus
            events that could trigger a re-render and shift the button position */}
        <button
          type="button"
          aria-label="Close"
          onMouseDown={(e) => {
            e.preventDefault();   // prevent focus change causing re-render
            e.stopPropagation();  // don't bubble to the card's stopPropagation
            onClose();
          }}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            zIndex: 20,
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            flexShrink: 0,
          }}
          className="dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="2" y1="2" x2="14" y2="14" />
            <line x1="14" y1="2" x2="2" y2="14" />
          </svg>
        </button>

        {invoice && (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 px-8 py-8 text-white overflow-hidden rounded-t-2xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 pointer-events-none" />
              <div className="relative z-10 flex items-center justify-between pr-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold tracking-tight">Invoice #{invoice.id}</h2>
                    <p className="text-blue-100 text-sm mt-0.5">{invoice.invoice_type}</p>
                  </div>
                </div>
                <StatusBadge status={invoice.status as string} />
              </div>
            </div>

            {/* Body */}
            <div className="px-8 py-6 space-y-6">
              {/* Invoice Info */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-wide">
                  Invoice Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Issue Date</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {moment.utc(invoice.created_at).local().format("MMM DD, YYYY")}
                    </p>
                  </div>
                  {invoice.paid_at && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Payment Date</p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{formatPaidAt(invoice.paid_at)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Period</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{invoice.invoice_period}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Number of Guests</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{invoice.number_of_guests}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Billing Type</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{invoice.billing_type}</p>
                  </div>
                  {invoice.no_of_months && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Duration</p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {invoice.no_of_months} {invoice.no_of_months === 1 ? "month" : "months"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-wide">
                  Price Breakdown
                </h3>
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    <div className="flex justify-between items-center px-5 py-4">
                      <span className="text-sm text-gray-700 dark:text-gray-300">First month's rent</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {`${currencySymbol()} ${Number(invoice.first_month_price)?.toFixed(2)}`}
                      </span>
                    </div>
                    {invoice.service_tax && Number(invoice.service_tax) > 0 && (
                      <div className="flex justify-between items-center px-5 py-4">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Service Fee</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {`${currencySymbol()} ${Number(invoice.service_tax)?.toFixed(2)}`}
                        </span>
                      </div>
                    )}
                    {invoice.extra_guest_amount && Number(invoice.extra_guest_amount) > 0 && (
                      <div className="flex justify-between items-center px-5 py-4">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Extra guest amount</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {`${currencySymbol()} ${Number(invoice.extra_guest_amount)?.toFixed(2)}`}
                        </span>
                      </div>
                    )}
                    {invoice.expenses?.length > 0 && invoice.expenses.map((expense: any, i: number) => (
                      <div key={`exp-${i}`} className="flex justify-between items-center px-5 py-4">
                        <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{expense.category}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {`${currencySymbol()} ${Number(expense.amount).toFixed(2)}`}
                        </span>
                      </div>
                    ))}
                    {invoice.discounts?.length > 0 && invoice.discounts.map((discount: any, i: number) => (
                      <div key={`dis-${i}`} className="flex justify-between items-center px-5 py-4 bg-green-50 dark:bg-green-900/20">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">DISCOUNT</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{discount.description}</span>
                        </div>
                        <span className="text-sm font-bold text-green-600 dark:text-green-400">
                          -{`${currencySymbol()} ${Number(discount.amount).toFixed(2)}`}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center px-5 py-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
                      <span className="text-lg font-bold text-gray-900 dark:text-gray-100">Total Amount</span>
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {`${currencySymbol()} ${Number(invoice.total)?.toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onMouseDown={() => onViewBooking(invoice.booking)}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
              >
                View Booking
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// ─── Main Invoice Page ────────────────────────────────────────────────────────

const Invoice: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [invoiceList, setInvoiceList] = useState<InvoiceDataExtended[]>([]);

  // ✅ Separate isOpen from selectedInvoice so modal DOM is stable
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDataExtended | null>(null);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    invoiceType: "Reservation fee",
    status: "Any",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState<TotalItems | null>(null);
  const navigate = useNavigate();

  const fetchInvoices = useCallback(
    async (pageNum = 1) => {
      setLoading(true);
      setError(null);
      setInvoiceList([]);
      try {
        const response = await dispatch(
          getInvoice({
            start_date: filters.fromDate,
            end_date: filters.toDate,
            type: filters.invoiceType,
            status: filters.status === "Any" ? undefined : filters.status,
            page: pageNum,
          })
        ).unwrap();
        if (response?.success) {
          setInvoiceList(response.data);
          setTotalItems(response);
          setTotalPages(response?.meta?.total_pages || 1);
        } else {
          setError(response?.error || "No invoices found.");
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || "Failed to fetch invoices.");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, filters]
  );

  useEffect(() => {
    setPage(1);
    fetchInvoices(1);
  }, [filters]);

  const openModal = (inv: InvoiceDataExtended) => {
    setSelectedInvoice(inv);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    // Keep selectedInvoice populated during close animation, clear after
    setTimeout(() => setSelectedInvoice(null), 200);
  }, []);

  const handleViewBooking = useCallback(
    (bookingId: number) => {
      setIsModalOpen(false);
      navigate(`/booking_request?id=${bookingId}`);
    },
    [navigate]
  );

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen">
      <div className="container mx-auto max-w-7xl">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-2">
            Invoices
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage and track all your invoices</p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">From Date</label>
              <CustomDatePickerV2
                placeholder="Select start date"
                value={filters.fromDate ? new Date(filters.fromDate) : null}
                onChange={(date: Date | null) =>
                  setFilters({
                    ...filters,
                    fromDate: date ? date.toLocaleDateString("sv-SE") : "",
                    toDate: date && filters.toDate && new Date(filters.toDate) < date ? "" : filters.toDate,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">To Date</label>
              <CustomDatePickerV2
                placeholder="Select end date"
                value={filters.toDate ? new Date(filters.toDate) : null}
                minDate={filters.fromDate ? moment(filters.fromDate).add(1, "day").toDate() : new Date()}
                onChange={(date: Date | null) =>
                  setFilters({ ...filters, toDate: date ? date.toLocaleDateString("sv-SE") : "" })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Invoice Type</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 p-3 rounded-xl bg-white dark:bg-gray-700 dark:text-white cursor-not-allowed opacity-60"
                value="Reservation fee"
                disabled
              >
                <option>Reservation fee</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Status</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 p-3 rounded-xl bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="Any">Any</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-2">Total Paid</p>
                <p className="text-3xl font-bold">
                  {totalItems ? `${currencySymbol()} ${Number(totalItems.confirmed_amt)?.toFixed(2)}` : "N/A"}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-2">Total Pending</p>
                <p className="text-3xl font-bold">
                  {totalItems ? `${currencySymbol()} ${Number(totalItems.invoice_amt)?.toFixed(2)}` : "N/A"}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 px-6 py-4">
            <div className="grid grid-cols-8 gap-4 font-semibold text-gray-700 dark:text-gray-200 text-sm">
              <div className="text-center">Invoice No.</div>
              <div className="text-center">Issue Date</div>
              <div className="text-center">Payment Date</div>
              <div className="text-center">Type</div>
              <div className="text-center">Billing</div>
              <div className="text-center">Status</div>
              <div className="text-center">Amount</div>
              <div className="text-center">Actions</div>
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              Array(5).fill(null).map((_, i) => (
                <div key={i} className="grid grid-cols-8 gap-4 px-6 py-5">
                  {Array(8).fill(null).map((_, j) => <Skeleton key={j} height={30} />)}
                </div>
              ))
            ) : invoiceList.length > 0 ? (
              invoiceList.map((inv) => (
                <div
                  key={inv.id}
                  className="grid grid-cols-8 gap-4 px-6 py-5 text-center hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                  onClick={() => openModal(inv)}
                >
                  <div className="font-semibold text-blue-600 dark:text-blue-400">#{inv.id}</div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">
                    {moment.utc(inv.created_at).local().format("MMM DD, YYYY")}
                  </div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">
                    {inv.paid_at ? formatPaidAt(inv.paid_at) : <span className="text-gray-400">-</span>}
                  </div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">{inv.invoice_type}</div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">{inv.billing_type}</div>
                  <div className="flex justify-center"><StatusBadge status={inv.status as string} /></div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {`${currencySymbol()} ${Number(inv.total)?.toFixed(2)}`}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/booking_request?id=${inv.booking}`); }}
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-sm transition-colors"
                    >
                      View Booking
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-16 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No invoices found</p>
                <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="bg-gray-50 dark:bg-gray-700/30 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) => { setPage(p); fetchInvoices(p); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal is always mounted once isOpen has been true, never conditionally */}
      <InvoiceDetailModal
        isOpen={isModalOpen}
        invoice={selectedInvoice}
        onClose={closeModal}
        onViewBooking={handleViewBooking}
      />
    </div>
  );
};

export default Invoice;